import { useState } from 'react';
import { DOC_TYPE_LABELS } from '../lib/documentStore';

const TYPE_ALIASES = Object.fromEntries([
  ...Object.entries(DOC_TYPE_LABELS).map(([k, v]) => [v.toLowerCase(), k]),
  ...Object.keys(DOC_TYPE_LABELS).map((k) => [k.toLowerCase(), k]),
  ['sailing instructions', 'si'],
  ['course card', 'course_card'],
  ['notice of race', 'nor'],
  ['race times', 'schedule'],
  ['other document', 'other_doc'],
  ['other link', 'other_link'],
  ['doc', 'other_doc'],
  ['link', 'other_link'],
]);

function resolveType(raw) {
  const t = raw.trim().toLowerCase();
  return TYPE_ALIASES[t] || null;
}

/**
 * Bulk paste import for documents.
 * Pipe-delimited format: id | title | type [| url]
 *
 * Example input:
 *   si-2026 | Sailing Instructions 2026 | si | https://example.com/si.pdf
 *   cc-red | Course Card - Red Fleet | course_card
 *   nor-summer | Notice of Race | nor | https://example.com/nor.pdf
 */
export default function DocumentsPasteImport({ existingDocIds = [], onImport, onClose }) {
  const [rawText, setRawText] = useState('');
  const [parsedResults, setParsedResults] = useState(null);

  function parseLine(line) {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('#')) {
      return { line: trimmed, status: 'skip', doc: null, message: 'Comment or empty' };
    }

    const parts = trimmed.split('|').map((p) => p.trim());

    if (parts.length < 3) {
      return {
        line: trimmed,
        status: 'error',
        doc: null,
        message: 'Expected: id | title | type [| url]',
      };
    }

    const id = parts[0];
    const title = parts[1];
    const typeRaw = parts[2];
    const url = parts[3] || '';

    if (!id) {
      return { line: trimmed, status: 'error', doc: null, message: 'Missing document ID' };
    }
    if (!title) {
      return { line: trimmed, status: 'error', doc: null, message: 'Missing title' };
    }

    const type = resolveType(typeRaw);
    if (!type) {
      const validTypes = Object.keys(DOC_TYPE_LABELS).join(', ');
      return {
        line: trimmed,
        status: 'error',
        doc: null,
        message: `Unknown type: "${typeRaw}". Valid: ${validTypes}`,
      };
    }

    const messages = [];
    if (existingDocIds.includes(id)) {
      messages.push(`ID "${id}" already exists — will be skipped`);
    }

    // Check for duplicate IDs within the paste
    const status = messages.length > 0 ? 'warn' : 'ok';
    return {
      line: trimmed,
      status,
      // Pasted docs land free (ra-hdil.1) — the app now honours this flag, and
      // a bulk import must not paywall a batch of NoRs by omission.
      doc: { id, title, type, url: url || null, isFree: true },
      message: messages.join('. ') || null,
    };
  }

  function handlePreview() {
    if (!rawText.trim()) return;
    const lines = rawText.trim().split('\n');
    const results = lines.map(parseLine);

    // Check for duplicate IDs within the paste itself
    const seenIds = new Set();
    for (const r of results) {
      if (r.doc && r.status !== 'skip') {
        if (seenIds.has(r.doc.id)) {
          r.status = 'warn';
          r.message = (r.message ? r.message + '. ' : '') + 'Duplicate ID in paste';
        }
        seenIds.add(r.doc.id);
      }
    }

    setParsedResults(results);
  }

  function handleImport() {
    if (!parsedResults) return;
    // Only import 'ok' items (skip warns for existing IDs)
    const docs = parsedResults
      .filter((r) => r.status === 'ok' && r.doc)
      .map((r) => r.doc);
    if (docs.length > 0) onImport(docs);
  }

  const okCount = parsedResults?.filter((r) => r.status === 'ok').length ?? 0;
  const warnCount = parsedResults?.filter((r) => r.status === 'warn').length ?? 0;
  const errorCount = parsedResults?.filter((r) => r.status === 'error').length ?? 0;

  return (
    <div className="paste-import">
      <div className="paste-import-header">
        <h3>Paste Import Documents</h3>
        {onClose && (
          <button className="btn btn-sm" onClick={onClose}>Close</button>
        )}
      </div>

      <details className="paste-import-classes">
        <summary>Document types</summary>
        <ul className="class-name-list">
          {Object.entries(DOC_TYPE_LABELS).map(([k, v]) => (
            <li key={k}><code>{k}</code> — {v}</li>
          ))}
        </ul>
      </details>

      <label className="form-field">
        <span className="form-label">
          Paste documents
          <span className="form-hint">
            Format: id | title | type [| url] (one per line)
          </span>
        </span>
        <textarea
          value={rawText}
          onChange={(e) => { setRawText(e.target.value); setParsedResults(null); }}
          placeholder={`si-2026 | Sailing Instructions 2026 | si | https://example.com/si.pdf\ncc-red | Course Card - Red Fleet | course_card\nnor-summer | Notice of Race | nor | https://example.com/nor.pdf`}
          rows={8}
          spellCheck={false}
          className="mono-input"
        />
      </label>

      <div className="paste-import-actions">
        <button
          className="btn btn-primary"
          onClick={handlePreview}
          disabled={!rawText.trim()}
        >
          Preview
        </button>
      </div>

      {parsedResults && (
        <div className="paste-import-results">
          <p className="paste-import-summary">
            {okCount} ready{warnCount > 0 ? `, ${warnCount} warnings` : ''}, {errorCount} failed,{' '}
            {parsedResults.filter((r) => r.status === 'skip').length} skipped
          </p>

          <table className="paste-results-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>ID</th>
                <th>Title</th>
                <th>Type</th>
                <th>URL</th>
                <th>Note</th>
              </tr>
            </thead>
            <tbody>
              {parsedResults
                .filter((r) => r.status !== 'skip')
                .map((r, i) => (
                  <tr key={i} className={`paste-result-${r.status}`}>
                    <td>
                      <span className={`status-dot ${r.status}`}>
                        {r.status === 'ok' ? 'OK' : r.status === 'warn' ? '!' : 'ERR'}
                      </span>
                    </td>
                    <td className="mono-text">{r.doc?.id || '—'}</td>
                    <td>{r.doc?.title || '—'}</td>
                    <td>{r.doc?.type ? DOC_TYPE_LABELS[r.doc.type] : '—'}</td>
                    <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.doc?.url || '—'}
                    </td>
                    <td>
                      {r.message && <span className={r.status === 'error' ? 'error-text' : 'warn-text'}>{r.message}</span>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>

          <button
            className="btn btn-primary"
            onClick={handleImport}
            disabled={okCount === 0}
          >
            Import {okCount} document{okCount !== 1 ? 's' : ''}
          </button>
        </div>
      )}
    </div>
  );
}
